import { Link } from '@/shared/ui/link';
import { usePathname } from '@/shared/lib/navigation';

import { useAuth } from '@/features/auth';
import { cn } from '@/shared/lib/utils';
import { activeNavItem, visibleGroups, type NavItem } from '@/widgets/sidebar/model/nav-config';

export function NavLink({
  item,
  isActive,
  onNavigate,
}: {
  item: NavItem;
  isActive: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      {item.title}
    </Link>
  );
}

/**
 * The desktop rail.
 *
 * Both this and the mobile drawer render `NAV_GROUPS` — see `model/nav-config`
 * for why that matters. The ten hand-repeated `hasXItems && (…)` blocks this
 * replaces were the reason a destination could exist here and nowhere else.
 */
export function Sidebar() {
  const pathname = usePathname();
  const { user } = useAuth();

  const groups = visibleGroups(user);
  const active = activeNavItem(pathname, groups);

  return (
    <aside className="hidden w-64 border-r bg-muted/40 lg:block">
      {/*
       * `pt-6` rather than a uniform `p-4`, so the rule under Dashboard lands on
       * the resource editor's pinned header border rather than 8px above it.
       * The two are siblings in the same flex row and start at the same y, so
       * the arithmetic is comparable: 24px here, plus the 36px nav link
       * (`text-sm`/`py-2`), plus the nav's 4px `gap-1`, plus the divider's 8px
       * `my-2`, puts its 1px border at 72-73 — which is exactly where
       * `EditPageHeader` ends (`py-4` around an `h-10` button, plus its border;
       * the same 73 the section rail pins to in `ResourceEditLayout`).
       *
       * It squares the ordinary pages too: 24px matches `<main>`'s `p-6`, so
       * the Dashboard label now centres on a `PageHeader` title instead of
       * sitting 8px high of it. Changing the padding, the link's `py-2` or the
       * divider's `my-2` moves the rule off the header.
       */}
      <div className="flex h-full flex-col gap-2 overflow-y-auto px-4 pb-4 pt-6">
        <nav aria-label="Main" className="flex flex-col gap-1">
          {groups.map((group, index) => (
            <div key={group.label ?? `group-${index}`} className="flex flex-col gap-1">
              {index > 0 && <div className="my-2 border-t border-border" />}
              {group.label && (
                <div className="px-3 py-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </p>
                </div>
              )}
              {group.items.map((item) => (
                <NavLink key={item.href} item={item} isActive={active?.href === item.href} />
              ))}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
